import React, { useContext } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TextField, TextFieldProps } from '../TextField';
import { PopoverContext } from '../Popover';
import { Badge } from '../Badge';
import { ColumnSelectOption } from '../ColumnSelect';

type AutocompleteInputProps<T extends string> = {
  id: string;
  selectedOptions: ColumnSelectOption<T>[];
  selectOption: (option: ColumnSelectOption<T>) => void;
  multiselect: boolean;
  onArrowDown: () => void;
  onArrowUp: () => void;
  onEnter: () => void;
} & TextFieldProps;

export function AutocompleteInput<T extends string>({
  id,
  selectedOptions,
  selectOption,
  multiselect,
  required,
  onArrowDown,
  onArrowUp,
  onEnter,
  onBlur,
  ...textFieldProps
}: AutocompleteInputProps<T>) {
  const { open, setOpen } = useContext(PopoverContext);

  function handleBlur() {
    onBlur && onBlur();
    setOpen(false);
  }

  function handleKeyPress(event: React.KeyboardEvent) {
    const { key } = event;

    if (key === 'Enter') {
      event.preventDefault();
      open && onEnter();
      return !multiselect && setOpen(!open);
    }
    if (key === 'ArrowDown') {
      event.preventDefault();
      onArrowDown();
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      onArrowUp();
    }

    setOpen(true);
  }

  return (
    <TextField
      id={id}
      prefix={
        multiselect &&
        selectedOptions.length > 0 && (
          <OptionList
            selectedOptions={selectedOptions}
            selectOption={selectOption}
          />
        )
      }
      checkDependentValidations={() => {
        if (multiselect && required && selectedOptions.length === 0) {
          return (
            textFieldProps.validationMessages?.valueMissing ||
            'Please select at least one option'
          );
        }

        return null;
      }}
      role="combobox"
      autoComplete="off"
      // Use aria-required for multiselect to avoid native required field validation and `checkDependentValidations` instead
      required={!multiselect && required}
      aria-required={multiselect && required}
      {...textFieldProps}
      onBlur={handleBlur}
      onKeyDown={handleKeyPress}
      onClick={() => setOpen(true)}
      suffix={
        <div className="absolute flex top-0 right-0 px-2 h-full">
          <ChevronDownIcon aria-hidden="true" className="w-4" />
        </div>
      }
    />
  );
}

type OptionListProps<T extends string> = {
  selectedOptions: ColumnSelectOption<T>[];
  selectOption: (option: ColumnSelectOption<T>) => void;
};

function OptionList<T extends string>({
  selectedOptions,
  selectOption
}: OptionListProps<T>) {
  return (
    <div className="flex flex-wrap gap-2">
      {selectedOptions.map((option, i) => (
        <Badge
          status="info"
          key={i}
          endIcon={<XMarkIcon className="w-3" />}
          onClick={e => {
            e?.stopPropagation();
            selectOption(option);
          }}
        >
          {option.label}
        </Badge>
      ))}
    </div>
  );
}
